import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import HeroSection from 'components/HeroSection';
import Container from 'components/Container';
import Stack from 'components/Stack';
import Text from 'components/Text';
import Link from 'components/Link';
import EmbeddedVideo from 'components/EmbeddedVideo';
import { PostShape } from 'utils/props';

import { media } from 'styles/utils';

const StyledBreadcrumb = styled(Link)`
  text-decoration: none;
`;

const StyledSection = styled(HeroSection)`
  overflow: visible;
`;

const StyledContainer = styled(Container)`
  margin-bottom: -10rem;
`;

const StyledAuthor = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const ProfilePhoto = styled(GatsbyImage)`
  border-radius: 100%;
  margin-right: 1rem;
  height: 3.25rem;
  width: 3.25rem;
`;

const StyledHeaderMedia = styled.div`
  overflow: hidden;
  border-radius: 0.5rem;
  width: ${({ isVideo }) => (isVideo ? '120%' : '100%')};
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  ${media.max('lg')} {
    width: 100%;
  }
`;

export default function PostHero({ post, postType }) {
  return (
    <StyledSection variant="minimal">
      <StyledContainer size="sm">
        <Stack gap="2rem" align="start">
          <Text variant="smallP" color="lightTeal">
            <StyledBreadcrumb to={`/resources/${post.category.slug}`}>
              {post.category.title}
            </StyledBreadcrumb>
            <span>{' | '}</span>
            <StyledBreadcrumb
              to={`/resources/${post.category.slug}/${post.subcategory.slug}`}
            >
              {post.subcategory.title}
            </StyledBreadcrumb>
          </Text>
          <StyledAuthor>
            {post.author?.image && (
              <ProfilePhoto
                image={getImage(post.author.image.asset)}
                alt={`Photograph of ${post.author.name}`}
              />
            )}
            <div>
              {post.author && (
                <Text variant="smallestP" color="white">
                  {post.author.name}
                </Text>
              )}
              <Text variant="smallestP" color="textGray">
                {post.publishedAt}
              </Text>
            </div>
          </StyledAuthor>
          <Text as="h1" variant="f1.resources" color="white">
            {post.title}
          </Text>
          <StyledHeaderMedia isVideo={post.videoPost}>
            {postType === 'video' && (
              <EmbeddedVideo title={post.title} src={post.videoLink} />
            )}
            {postType === 'text' && post.featureImage && (
              <GatsbyImage
                image={getImage(post.featureImage?.image.asset)}
                alt={post.featureImage?.alt || post.title}
              />
            )}
          </StyledHeaderMedia>
        </Stack>
      </StyledContainer>
    </StyledSection>
  );
}

PostHero.propTypes = {
  post: PostShape,
  postType: PropTypes.oneOf(['video', 'text']),
};
